import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppState } from '../../core';
import { CorrelationRecord, isAndroid, isIos } from '../../core/models/correlation-record';
import { PathWaypointService, PostProcessingService } from '../../core/services';
import { StatGroup, StatMetric, TableColumn, TableViewComponentBase, TableViewQueryRequest } from '../../shared/components/table-view/table-view-component-base';
import { PathKmlViewerModal } from '../../path/path-kml-viewer/path-kml-viewer-modal/path-kml-viewer-modal.component';
import { firstValueFrom, timer } from 'rxjs';

const defaultMetrics: StatMetric[] = [
  { column: 'callduration', statTypes: ['min','max','avg']},
  { column: 'callsetup', statTypes: ['min','max','avg']},
  { column: 'calltraffic', statTypes: ['min','max','avg']},
  { column: 'timetofix', statTypes: ['min','max','avg']},
  { column: 'horizontalerror', statTypes: ['min','max','avg']},
  { column: 'verticalerror', statTypes: ['min','max','avg']},  
];
const metricsWithCallTime: StatMetric[] = [
  ...defaultMetrics,
  { column: 'callstarttime', statTypes: ['min','max'], format: { dataType: 'date', layout: 'short' } },
];
defaultMetrics.push({ column: '*', format: { dataType: 'number' }, statTypes: ['count']},);
metricsWithCallTime.push({ column: '*', format: { dataType: 'number' }, statTypes: ['count']},);

export const correlationDefaultStatGroups: StatGroup[] = [
  {
    id: 'byCarrier',
    groupBy: ['carrier'],
    visible: true,
    metrics: defaultMetrics
  },
  {
    id: 'byOs',
    title: 'OS',
    groupBy: ['os'],
    metrics: defaultMetrics
  }, 
  {
    id: 'byBuilding',
    title: 'Building',
    groupBy: ['buildingid'],
    metrics: metricsWithCallTime
  },
  {
    id: 'byMarket',
    groupBy: ['market'],
    metrics: metricsWithCallTime
  },
  {
    id: 'byStage',
    groupBy: ['stage'],
    metrics: metricsWithCallTime
  },
  {
    id: 'byPath',
    title: 'Path',
    groupBy: ['pathid'],
    metrics: metricsWithCallTime
  },
  {
    id: 'byTestType',
    title: 'Test Type',
    groupBy: ['calltesttype'],
    visible: false,
    metrics: metricsWithCallTime
  },
  {
    id: 'byModel',
    title: 'Model',
    groupBy: ['modelname'],
    metrics: metricsWithCallTime
  }, 
  {
    id: 'byCarrierAndModel',
    title: 'Carrier/Make/Model',
    groupBy: ['carrier', 'make','modelname'],
    metrics: defaultMetrics
  },
];

@Component({
  selector: 'app-correlation-table',
  templateUrl: '../../shared/components/table-view/table-view.component.html',
  styleUrls: [
    '../../shared/components/table-view/scss/table-view.scss',
    '../../shared/components/table-view/scss/app-tab-full-height.scss',
    '../../shared/components/table-view/scss/fixed-headers-and-column.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CorrelationTableComponent extends TableViewComponentBase<CorrelationRecord> implements OnInit, OnDestroy {


  constructor(
    appState: AppState,
    modalService: BsModalService,
    public postProcessingService: PostProcessingService,
    public pathWaypointService: PathWaypointService,
    elementRef: ElementRef,
    datePipe: DatePipe
  ) {
    super(appState, modalService, elementRef, datePipe)

    //TODO - define each columnMeta explicitly rather than loop below
    let idColumnMeta: any = {
      name: 'viewrowid',
      visible: false,
      title: "View Row ID"
    };
    this.tableColumns = [idColumnMeta];
    for (let objKey in new CorrelationRecord()) {
      if (this.tableColumns.some(tc => tc.name == objKey)) {
        continue;
      }
      let columnMeta: any = { name: objKey, visible: true }
      switch (objKey) {
        case 'buildingid':
          columnMeta.template = 'buildingid'
          break;
        case 'pointid':
          columnMeta.template = 'pointid'
          break;
        case 'morphology':
        case 'pathid':
        case 'routeid':
        case 'floornum':
        case 'pointidnum':
        case 'callimportfileid':
        case 'timelogendtime':
        case 'callendtime':
        case 'starttimeoffset':
        case 'avgbarometricpressure':
        case 'comments':
          columnMeta.visible = false;
          break;
        case 'timeoffset':
        case 'callduration':
        case 'callsetup':
        case 'calltraffic':
        case 'latitude':
        case 'longitude':
        case 'uncertaintyhoriz':
        case 'altitude':
        case 'uncertaintyvert':
        case 'horizontalerror':
        case 'altitudeerror':
        case 'verticalerror':
        case 'avgbarometricpressure':
            columnMeta.type = 'number';
          break;
        case 'sourcetable':
          columnMeta.visible = false;
          columnMeta.template = 'sourcetable'
          break;
        case 'locationtime':
          columnMeta.template = 'locationtime'
          break;
        case 'callstarttime':
          columnMeta.template = 'callstarttime'
          break;
        case 'callsourceid':
          columnMeta.template = 'callsourceid'
          break;
        case 'timetofix':
          columnMeta.title = "Time To Fix"
          columnMeta.type = 'number'
          break;
        case 'groupingfield':
          columnMeta.title = "Group"
          break;
        case 'heightoffset':
          columnMeta.title = "Height Offset"
          columnMeta.type = 'number'
          break;
        case 'modelname':
          columnMeta.title = "Model Name"
          break;
        case 'avgbarometricpressure':
          columnMeta.title = "Average Barometric Pressure"
          columnMeta.template = 'avgbarometricpressure'
          columnMeta.type = 'number'
          break;
        case 'timelogstarttime':
          columnMeta.title = "Time Log Start (UTC)"
          columnMeta.template = 'time'
          break;
        case 'timelogendtime':
          columnMeta.title = "Time Log End (UTC)"
          columnMeta.template = 'time'
          break;
        case 'starttimeoffset':
          columnMeta.title = "Start Offset (UTC)"
          columnMeta.type = 'time'
          break;
        case 'callendtime':
          columnMeta.title = "Call End (UTC)"
          columnMeta.template = 'time'
          break;
        case 'correlationtime':
          columnMeta.title = "Correlated (UTC)"
          columnMeta.template = 'time'
          break;
        case 'completedcall':
        case 'correlatedcall':
          columnMeta.title = this.createColumnTitle(columnMeta.name).replace(/\s*Call\s*$/i, '');
          columnMeta.template = 'boolean';
          break;
        case 'validhorizlocation':
        case 'validvertlocation':
          columnMeta.template = 'boolean'
          break;
        case 'calltesttype':
          columnMeta.title = 'Test Type'
          break;
        case 'userid':
          columnMeta.title = 'User ID'
          columnMeta.visible = false;
          columnMeta.template = 'editUser'
          break;
        case 'username':
          columnMeta.title = 'User Name'
          columnMeta.visible = false;
          columnMeta.template = 'editUser'
          break;
        case 'taskid':
          columnMeta.title = 'Task'
          columnMeta.visible = false;
          break;
        case 'testpointid':
          columnMeta.title = 'Test Point'
          columnMeta.visible = false;
          break;
      }
      if (!columnMeta.title) {
        columnMeta.title = this.createColumnTitle(columnMeta.name)
      }
      this.tableColumns.push(columnMeta);
    }
    this.statGroups = [...correlationDefaultStatGroups];
    this.idColumns = [idColumnMeta];
    this.sortColumns = [
      { key: 'timelogstarttime', order: 'DESC' },
      { key: 'callstarttime', order: 'DESC' },
      { key: 'type', order: 'ASC' },
    ];
  }


  ngOnInit(): void {
    this.init()
  }


  ngOnDestroy(): void {
    this.destroy()
  }

  override updateColumnsForView(viewName:string){
    if ( viewName === 'testingContext') {
      this.columnByName['taskid'].visible = true;
      this.columnByName['testpointid'].visible = true;
    }
  }

  isDateColumn = (column: TableColumn) => {
    return column.template == 'time' || ['locationtime', 'callstarttime'].includes(column.name)
  }

  doQuery = async (queryReq: TableViewQueryRequest) => this.postProcessingService.findCorrelationRecords(queryReq);

  getName = () => 'correlation'


  showingKmlViewer = false;
  
  async showKmlViewer() {
    this.showingKmlViewer = true;
    const lastIsActiveView = this.isActiveView;
    this.isActiveView = false;
    const records = this.records;
    try {
      await firstValueFrom(timer(0));
      const uniqueBuildingIds = this.records.map(r => r.buildingid).filter((id, i, a) => a.indexOf(id) == i);
      const paths = (await Promise.all(uniqueBuildingIds.map(buildingId =>
        this.pathWaypointService.findBuilding3dPaths(buildingId)
      ))).flat();
      // console.log('showKmlViewer()', { uniqueBuildingIds, paths, records });
      for (const path of paths) {
        for (const wpRow of path.mapWaypointRows) {
          const mpt = wpRow.mapWaypoint;
          wpRow.pointId = mpt.pointId || mpt.exPointId;
          const corrLogCallPts = records.filter(rec => rec.pathid == path.pathId
            && rec.pointid && (rec.pointid === mpt.pointId || rec.pointid === mpt.exPointId)
            && rec.latitude != null && rec.longitude != null && rec.altitude != null);

          if (!mpt.miscChildren) {
            mpt.miscChildren = [];
          }
          corrLogCallPts.forEach(cr => {
            // console.log(`showKmlViewer() adding misc pt to ${mpt.pointId || mpt.exPointId}`, { cr });
            if (cr.latitude != null && cr.longitude != null) {
              const child = {
                latitude: Number(cr.latitude),
                longitude: Number(cr.longitude),
                altitudeEllipsoidMeters: Number(cr.altitude),
                id: cr.viewrowid,
                parentId: mpt.id,
                title: `${cr.pointid} | ${cr.horizontalerror?.toFixed(2)}m | ${cr.carrier} | ${cr.type} | ${cr.summarytech}`,
                type: 'call log',
                iconClass: isAndroid(cr.os) ? 'fab fa-android android-icon' : isIos(cr.os) ? 'fab fa-apple apple-icon' : undefined,
                data: cr
              };
              mpt.miscChildren.push(child);
            }
          });
        }
        path.mapWaypointRows.forEach(mwpRow=>{
          mwpRow.mapWaypoint.disabled = !mwpRow.mapWaypoint.miscChildren?.length;
          // console.log(`${mwpRow.pointId} disabled: ${mwpRow.mapWaypoint.disabled}`, { mwpRow });
        });

      }

      const modalRef = PathKmlViewerModal.show(this.modalService, {
      selectedPathIds: [],
      buildingId: uniqueBuildingIds[0],
      paths
    });
    await firstValueFrom(modalRef.onHidden);
  } finally {
    this.showingKmlViewer = false;
    this.isActiveView = lastIsActiveView;
  }
  }
}
